import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { formatDistanceToNowLocale } from '@/lib/date-locale';

interface PresenceState {
  user_id: string;
  status: 'online' | 'away' | 'offline';
  last_seen_at: string;
  device_type: 'web' | 'ios' | 'android';
}

interface PresenceContextValue {
  onlineUsers: string[];
  isUserOnline: (userId: string) => boolean;
  getUserPresence: (userId: string) => PresenceState | null;
  getLastSeen: (userId: string) => string;
  fetchContactsPresence: (contactUserIds: string[]) => Promise<void>;
}

const PresenceContext = createContext<PresenceContextValue | null>(null);

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [presenceMap, setPresenceMap] = useState<Map<string, PresenceState>>(new Map());
  const channelRef = useRef<RealtimeChannel | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const isTrackingRef = useRef(false);

  // Update presence in database
  const updatePresence = useCallback(async (status: 'online' | 'away' | 'offline') => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const deviceType = /Mobile|Android|iPhone/i.test(navigator.userAgent)
      ? (/Android/i.test(navigator.userAgent) ? 'android' : 'ios')
      : 'web';

    await supabase
      .from('user_presence')
      .upsert({
        user_id: user.id,
        status,
        last_seen_at: new Date().toISOString(),
        device_type: deviceType,
      }, { onConflict: 'user_id' });
  }, []);

  // Fetch presence from DB for a list of contact user IDs
  const fetchContactsPresence = useCallback(async (contactUserIds: string[]) => {
    if (!contactUserIds.length) return;

    const { data } = await supabase
      .from('user_presence')
      .select('user_id, status, last_seen_at, device_type')
      .in('user_id', contactUserIds);

    if (!data) return;

    const newOnlineFromDb: string[] = [];

    setPresenceMap(prev => {
      const next = new Map(prev);
      for (const row of data) {
        const existing = next.get(row.user_id);
        // Only set from DB if not already tracked via Realtime
        if (!existing || existing.status === 'offline') {
          const ps: PresenceState = {
            user_id: row.user_id,
            status: row.status as PresenceState['status'],
            last_seen_at: row.last_seen_at,
            device_type: row.device_type as PresenceState['device_type'],
          };
          next.set(row.user_id, ps);
        }
        // Track online users from DB
        const finalStatus = next.get(row.user_id)?.status;
        if (finalStatus === 'online') {
          newOnlineFromDb.push(row.user_id);
        }
      }
      return next;
    });

    // Sync onlineUsers with DB-fetched online statuses
    setOnlineUsers(prev => {
      const combined = new Set([...prev, ...newOnlineFromDb]);
      // Remove users that DB says are not online (and not tracked by Realtime)
      for (const uid of contactUserIds) {
        if (!newOnlineFromDb.includes(uid) && !prev.includes(uid)) {
          combined.delete(uid);
        }
      }
      const arr = Array.from(combined);
      // Only update if changed
      if (arr.length === prev.length && arr.every(u => prev.includes(u))) return prev;
      return arr;
    });
  }, []);

  // Start tracking current user's presence
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const startTracking = async () => {
      if (isTrackingRef.current) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      isTrackingRef.current = true;
      await updatePresence('online');

      channelRef.current = supabase
        .channel('user-presence')
        .on('presence', { event: 'sync' }, () => {
          const state = channelRef.current?.presenceState() || {};
          const users: string[] = [];
          const newMap = new Map<string, PresenceState>();

          Object.values(state).forEach((presences: any) => {
            presences.forEach((presence: any) => {
              if (presence.user_id) {
                users.push(presence.user_id);
                newMap.set(presence.user_id, {
                  user_id: presence.user_id,
                  status: 'online',
                  last_seen_at: presence.online_at || new Date().toISOString(),
                  device_type: presence.device_type || 'web',
                });
              }
            });
          });

          setOnlineUsers(users);
          setPresenceMap(prev => {
            const merged = new Map(prev);
            for (const [uid, ps] of merged) {
              if (ps.status === 'online' && !newMap.has(uid)) {
                merged.set(uid, { ...ps, status: 'offline', last_seen_at: new Date().toISOString() });
              }
            }
            for (const [uid, ps] of newMap) {
              merged.set(uid, ps);
            }
            return merged;
          });
        })
        .on('presence', { event: 'join' }, ({ newPresences }) => {
          newPresences.forEach((presence: any) => {
            if (presence.user_id) {
              setOnlineUsers(prev => [...new Set([...prev, presence.user_id])]);
              setPresenceMap(prev => {
                const next = new Map(prev);
                next.set(presence.user_id, {
                  user_id: presence.user_id,
                  status: 'online',
                  last_seen_at: presence.online_at || new Date().toISOString(),
                  device_type: presence.device_type || 'web',
                });
                return next;
              });
            }
          });
        })
        .on('presence', { event: 'leave' }, ({ leftPresences }) => {
          leftPresences.forEach((presence: any) => {
            if (presence.user_id) {
              setOnlineUsers(prev => prev.filter(id => id !== presence.user_id));
              setPresenceMap(prev => {
                const next = new Map(prev);
                next.set(presence.user_id, {
                  user_id: presence.user_id,
                  status: 'offline',
                  last_seen_at: new Date().toISOString(),
                  device_type: presence.device_type || 'web',
                });
                return next;
              });
            }
          });
        })
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            await channelRef.current?.track({
              user_id: user.id,
              online_at: new Date().toISOString(),
              device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent)
                ? (/Android/i.test(navigator.userAgent) ? 'android' : 'ios')
                : 'web',
            });
          }
        });

      // Heartbeat every 2 minutes
      heartbeatRef.current = setInterval(() => {
        updatePresence('online');
      }, 120000);

      // Visibility change
      const handleVisibilityChange = () => {
        if (document.hidden) {
          updatePresence('away');
        } else {
          updatePresence('online');
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Unload
      const handleUnload = () => {
        updatePresence('offline');
      };
      window.addEventListener('beforeunload', handleUnload);

      cleanup = () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('beforeunload', handleUnload);
      };
    };

    // Listen for auth changes to start/stop tracking
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        startTracking();
      } else if (event === 'SIGNED_OUT') {
        isTrackingRef.current = false;
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        if (channelRef.current) channelRef.current.unsubscribe();
        updatePresence('offline');
      }
    });

    // Also try to start immediately if already authed
    startTracking();

    return () => {
      subscription.unsubscribe();
      cleanup?.();
      isTrackingRef.current = false;
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (channelRef.current) channelRef.current.unsubscribe();
    };
  }, [updatePresence]);

  // Memoized helpers
  const isUserOnline = useCallback((userId: string): boolean => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  const getUserPresence = useCallback((userId: string): PresenceState | null => {
    return presenceMap.get(userId) || null;
  }, [presenceMap]);

  const getLastSeen = useCallback((userId: string): string => {
    if (onlineUsers.includes(userId)) return 'Online';

    const presence = presenceMap.get(userId);
    if (!presence?.last_seen_at) return 'elefa user';

    try {
      const lastSeen = new Date(presence.last_seen_at);
      const diffMs = Date.now() - lastSeen.getTime();
      if (diffMs < 60_000) return 'Last seen just now';
      return `Last seen ${formatDistanceToNowLocale(lastSeen, { addSuffix: true })}`;
    } catch {
      return '';
    }
  }, [onlineUsers, presenceMap]);

  return (
    <PresenceContext.Provider value={{
      onlineUsers,
      isUserOnline,
      getUserPresence,
      getLastSeen,
      fetchContactsPresence,
    }}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence(): PresenceContextValue {
  const ctx = useContext(PresenceContext);
  if (!ctx) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return ctx;
}
